import React, { useState } from 'react'
import EditIcon from '@material-ui/icons/Edit'
import DOMPurify from 'dompurify'

import { ERRORS } from 'consts'
import { useApi } from 'hooks'
import { snackbar } from 'components'
import { GetTagsResponse, Tag } from 'features/Main/Tags/types'
import { getActiveTags } from 'features/Main/Tags/api'

import { GetMembershipsResponse, Membership } from '../../Memberships/types'
import { getMemberships } from '../../Memberships/api'
import { DefaultTable } from '../../components'
import { getMembershipsPrizes } from '../../Memberships/Prizes/api'
import { GetPrizesResponse, Prize } from '../../Prizes/types'
import { REWARD_TYPE_BY_MEMBERSHIP } from '../../Memberships/Prizes/components/constants'
import {
  CreatePrizeDialog,
  DeletePrizeDialog,
  UpdatePrizeDialog,
} from '../../Memberships/Prizes/components'

const columns = [
  { title: 'ID', field: 'id' },
  {
    title: 'Título',
    field: 'title',
    render: (row: { title: string }) => (
      // eslint-disable-next-line react/no-danger
      <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(row.title) }} />
    ),
  },
  {
    title: 'Descripción',
    field: 'shortTitle',
  },
  {
    title: 'Texto adicional',
    field: 'additionalText',
  },
  {
    title: 'Tipo',
    render: (row: Prize) =>
      REWARD_TYPE_BY_MEMBERSHIP.find(value => value.id === row.rewardType)?.description,
    sorting: false,
  },
]

const getPrizeResponseGetter = (responseData: GetPrizesResponse) => responseData?.prizes ?? []
const getTagsResponseGetter = (responseData: GetTagsResponse) => responseData?.tags ?? []

const Benefits: React.FC = () => {
  const [{ data: prizes, isLoading }, , setPrizes] = useApi<GetPrizesResponse, Prize[]>(
    () => getMembershipsPrizes(),
    getPrizeResponseGetter,
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )
  const [{ data: tags }] = useApi<GetTagsResponse, Tag[]>(getActiveTags, getTagsResponseGetter, {
    baseData: [],
    onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE),
  })
  const [{ data: memberships }] = useApi<GetMembershipsResponse, Membership[]>(
    getMemberships,
    response => response?.memberships ?? [],
    { baseData: [], onError: () => snackbar.show(ERRORS.GENERIC_ERROR_MESSAGE) },
  )

  const [isCreatePrizeDialogOpen, setIsCreatePrizeDialogOpen] = useState(false)
  const [isDeletePrizeDialogOpen, setIsDeletePrizeDialogOpen] = useState(false)
  const [prizeToDelete, setPrizeToDelete] = useState<Prize | null>(null)
  const [isUpdatePrizeDialogOpen, setIsUpdatePrizeDialogOpen] = useState(false)
  const [prizeToUpdate, setPrizeToUpdate] = useState<Prize | null>(null)

  const handleCreatePrizeDone = (newPrize: Prize) => {
    setPrizes([newPrize, ...prizes])
    setIsCreatePrizeDialogOpen(false)
  }

  const handleDeletePrizeDialogClose = () => {
    setIsDeletePrizeDialogOpen(false)
    setPrizeToDelete(null)
  }

  const handleDeletePrizeDone = () => {
    if (!prizeToDelete) return
    setPrizes(prizes.filter((prize: Prize) => prize.id !== prizeToDelete.id))
    handleDeletePrizeDialogClose()
  }

  const handleUpdatePrizeDialogClose = () => {
    setIsUpdatePrizeDialogOpen(false)
    setPrizeToUpdate(null)
  }

  const handleUpdatePrizeDone = (updatedPrize: Prize) => {
    setPrizes(
      prizes.map((prize: Prize) => {
        if (prize.id === updatedPrize.id) {
          return updatedPrize
        }
        return prize
      }),
    )
    handleUpdatePrizeDialogClose()
  }

  const actions = [
    {
      icon: 'add',
      tooltip: 'Nuevo beneficio',
      isFreeAction: true,
      onClick: () => setIsCreatePrizeDialogOpen(true),
    },
    {
      icon: () => <EditIcon />,
      tooltip: 'Editar premio',
      onClick: (_: React.SyntheticEvent, row: Prize | Prize[]) => {
        setPrizeToUpdate(row as Prize)
        setIsUpdatePrizeDialogOpen(true)
      },
    },
    {
      icon: 'delete',
      tooltip: `Eliminar`,
      onClick: (_: React.SyntheticEvent, row: Prize | Prize[]) => {
        setPrizeToDelete(row as Prize)
        setIsDeletePrizeDialogOpen(true)
      },
    },
  ]

  return (
    <>
      <DefaultTable
        title="Gestor de beneficios"
        columns={columns}
        data={prizes}
        isLoading={isLoading}
        actions={actions}
        options={{ pageSize: 10 }}
      />
      {isCreatePrizeDialogOpen && memberships.length > 0 && (
        <CreatePrizeDialog
          handleClose={() => setIsCreatePrizeDialogOpen(false)}
          onDone={handleCreatePrizeDone}
          membershipId={memberships[0].id}
          tags={tags}
          memberships={memberships}
        />
      )}
      {isDeletePrizeDialogOpen && prizeToDelete && (
        <DeletePrizeDialog
          handleClose={handleDeletePrizeDialogClose}
          prizeId={prizeToDelete.id}
          prizeTitle="Eliminar premio"
          onDone={handleDeletePrizeDone}
        />
      )}
      {isUpdatePrizeDialogOpen && prizeToUpdate && (
        <UpdatePrizeDialog
          handleClose={handleUpdatePrizeDialogClose}
          prizeToUpdate={prizeToUpdate}
          onDone={handleUpdatePrizeDone}
          tags={tags}
          memberships={memberships}
        />
      )}
      {/* Crear beneficio global queda pendiente hasta definir asignación multi-nivel */}
      {/* <CreatePrizeDialog ... /> */}
    </>
  )
}

export { Benefits }
